import { useMemo } from 'react';

import Tabs from '@/components/Tabs';
import { Seed } from '@/types';
import { Regex } from '@/utils/regex.util';

/**
 * Tab configuration for the seed drawer
 */
interface TabConfig {
  /** Unique tab identifier */
  value: string;
  /** Display label for the tab */
  label: string;
}

/**
 * Props for the SeedDrawerTabs component
 */
interface SeedDrawerTabsProps {
  /** The seed object to determine available tabs */
  seed: Seed;
  /** Currently active tab value */
  activeTab: string;
  /** Callback when tab selection changes */
  onTabChange: (tabValue: string) => void;
}

/**
 * SeedDrawerTabs Component
 *
 * Manages tab configuration and navigation for the seed drawer.
 * Dynamically generates tab configuration based on seed type:
 *
 * - **Web Application seeds**: Authentication, Sitemap, History, Notes
 * - **IP Address seeds**: IP Address Details, History, Notes
 * - **Domain seeds**: Domain Details, Domain History, History, Notes
 *
 * The component handles:
 * - Type-specific tab generation based on seed.class and identifier
 * - Active tab state management with empty keyboard dependencies (tabs stable across row navigation)
 * - Tab selection callbacks to parent component
 *
 * @example
 * ```tsx
 * <SeedDrawerTabs
 *   seed={seed}
 *   activeTab={currentTab}
 *   onTabChange={(tabValue) => setState({ currentTab: tabValue })}
 * />
 * ```
 */
export function SeedDrawerTabs({
  seed,
  activeTab,
  onTabChange,
}: SeedDrawerTabsProps) {
  // Determine seed type for tab configuration
  const isIP = useMemo(
    () =>
      seed?.identifier
        ? Boolean(
            Regex.IPV4.test(seed.identifier) || Regex.IPV6.test(seed.identifier)
          )
        : false,
    [seed?.identifier]
  );

  const isWebapp = seed?.class === 'webapplication';

  // Build tab configuration based on seed type
  const tabs = useMemo<TabConfig[]>(() => {
    if (isWebapp) {
      return [
        {
          value: 'authentication',
          label: 'Authentication',
        },
        {
          value: 'sitemap',
          label: 'Sitemap',
        },
        {
          value: 'seedHistory',
          label: 'History',
        },
        {
          value: 'seedNotes',
          label: 'Notes',
        },
      ];
    }

    if (isIP) {
      return [
        {
          value: 'details',
          label: 'IP Address Details',
        },
        {
          value: 'seedHistory',
          label: 'History',
        },
        {
          value: 'seedNotes',
          label: 'Notes',
        },
      ];
    }

    // Default: domain tabs
    return [
      {
        value: 'details',
        label: 'Domain Details',
      },
      {
        value: 'domainHistory',
        label: 'Domain History',
      },
      {
        value: 'seedHistory',
        label: 'History',
      },
      {
        value: 'seedNotes',
        label: 'Notes',
      },
    ];
  }, [isWebapp, isIP]);

  // Find current tab object or fall back to first tab
  const currentTab = useMemo(
    () => tabs.find(tab => tab.value === activeTab) || tabs[0],
    [tabs, activeTab]
  );

  return (
    <Tabs
      id="seedDetailsTabs"
      tabs={tabs}
      selected={currentTab}
      keyboardDependencies={[]}
      onSelect={option => {
        onTabChange(option.value);
      }}
    />
  );
}
